import React, { useState, useRef, useEffect } from 'react'
import { X, Play, Pause, Download, Save, Volume2 } from 'lucide-react'
import AudioForm from './AudioForm.jsx'
import toast from 'react-hot-toast'

function AudioModal({ 
  audioData, 
  onSave, 
  onClose, 
  isEditing = false 
}) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [showForm, setShowForm] = useState(isEditing)
  const audioRef = useRef(null)
  
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime)
    const handleDurationChange = () => setDuration(audio.duration)
    const handleEnded = () => setIsPlaying(false)
    
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('durationchange', handleDurationChange)
    audio.addEventListener('ended', handleEnded)
    
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('durationchange', handleDurationChange)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [])
  
  const handlePlayPause = () => {
    const audio = audioRef.current
    if (!audio) return
    
    if (isPlaying) {
      audio.pause()
    } else {
      audio.play().catch(error => {
        console.error('Error playing audio:', error)
        toast.error('Failed to play audio')
      })
    }
    setIsPlaying(!isPlaying)
  }
  
  const handleSeek = (e) => {
    const audio = audioRef.current
    if (!audio || !duration) return
    
    const rect = e.currentTarget.getBoundingClientRect()
    const percent = (e.clientX - rect.left) / rect.width
    const newTime = percent * duration
    
    audio.currentTime = newTime
    setCurrentTime(newTime)
  }
  
  const handleDownload = async () => {
    try {
      const response = await fetch(audioData.audioUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${audioData.title || 'audio'}.wav`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Audio downloaded!')
    } catch (error) {
      toast.error('Failed to download audio')
      console.error(error)
    }
  }
  
  const handleSave = (formData) => {
    const savedAudio = {
      ...audioData,
      ...formData,
      duration: duration || audioData.duration
    }
    onSave(savedAudio)
  }
  
  const formatTime = (time) => {
    if (!time || !isFinite(time)) return '0:00'
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }
  
  const progressPercent = duration ? (currentTime / duration) * 100 : 0
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Audio' : 'Preview & Save Audio'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Audio Player */}
        <div className="p-6 border-b border-gray-200">
          <audio ref={audioRef} src={audioData.audioUrl} />
          
          <div className="space-y-4">
            {/* Audio Info */}
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {audioData.title || 'Untitled Audio'}
              </h3>
              <p className="text-sm text-gray-600 line-clamp-3">
                {audioData.text}
              </p>
            </div>
            
            {/* Progress Bar */}
            <div className="space-y-2">
              <div 
                className="w-full h-2 bg-gray-200 rounded-full cursor-pointer overflow-hidden"
                onClick={handleSeek}
              >
                <div 
                  className="h-full bg-primary-500 transition-all duration-100"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
            
            {/* Controls */}
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={handlePlayPause}
                className="btn-primary p-4 rounded-full"
              >
                {isPlaying ? (
                  <Pause className="w-6 h-6" />
                ) : (
                  <Play className="w-6 h-6" />
                )}
              </button>
              
              <button
                onClick={handleDownload}
                className="btn-secondary flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
            
            {/* Voice Settings Display */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <div className="text-sm font-medium text-gray-900">
                  {audioData.voiceSettings?.gender || 'Unknown'}
                </div>
                <div className="text-xs text-gray-600">Gender</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-900">
                  {audioData.voiceSettings?.accent || 'en-US'}
                </div>
                <div className="text-xs text-gray-600">Accent</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-900">
                  {audioData.voiceSettings?.speed?.toFixed(1) || '1.0'}x
                </div>
                <div className="text-xs text-gray-600">Speed</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-900">
                  {audioData.voiceSettings?.pitch?.toFixed(1) || '1.0'}
                </div>
                <div className="text-xs text-gray-600">Pitch</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Form or Actions */}
        <div className="p-6">
          {showForm ? (
            <AudioForm
              initialData={audioData}
              onSave={handleSave}
              onCancel={() => setShowForm(false)}
              isEditing={isEditing}
            />
          ) : (
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowForm(true)}
                className="btn-secondary flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isEditing ? 'Edit Details' : 'Save with Details'}
              </button>
              
              {!isEditing && (
                <button
                  onClick={() => handleSave({ title: audioData.title || `Audio ${Date.now()}` })}
                  className="btn-primary flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Quick Save
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AudioModal
